/**
 * Members API endpoints for managing site members and subscription tiers.
 */

import { api } from './client';
import type {
  Member,
  MemberStats,
  SubscriptionTier,
  CreateMemberRequest,
  UpdateMemberRequest,
  CreateTierRequest,
  ApiResponse,
} from '../types';

/** List params */
export interface ListMembersParams {
  limit?: number;
  offset?: number;
  status?: string;
}

/** Build query string from params */
function buildQuery(params?: ListMembersParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  if (params.status) query.set('status', params.status);
  const str = query.toString();
  return str ? `?${str}` : '';
}

/** Members API */
export const membersApi = {
  /** List members for a site */
  list: (siteSlug: string, params?: ListMembersParams) =>
    api.get<ApiResponse<Member[]>>(`/v1/sites/${siteSlug}/members${buildQuery(params)}`),

  /** Get a member by ID */
  get: (siteSlug: string, memberId: string) =>
    api.get<ApiResponse<Member>>(`/v1/sites/${siteSlug}/members/${memberId}`),

  /** Create a new member */
  create: (siteSlug: string, data: CreateMemberRequest) =>
    api.post<ApiResponse<Member>>(`/v1/sites/${siteSlug}/members`, data),

  /** Update a member */
  update: (siteSlug: string, memberId: string, data: UpdateMemberRequest) =>
    api.put<ApiResponse<Member>>(`/v1/sites/${siteSlug}/members/${memberId}`, data),

  /** Delete a member */
  delete: (siteSlug: string, memberId: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/members/${memberId}`),

  /** Get member statistics */
  stats: (siteSlug: string) =>
    api.get<ApiResponse<MemberStats>>(`/v1/sites/${siteSlug}/members/stats`),
};

/** Subscription Tiers API */
export const tiersApi = {
  /** List tiers for a site */
  list: (siteSlug: string) =>
    api.get<ApiResponse<SubscriptionTier[]>>(`/v1/sites/${siteSlug}/tiers`),

  /** Get a tier by ID */
  get: (siteSlug: string, tierId: string) =>
    api.get<ApiResponse<SubscriptionTier>>(`/v1/sites/${siteSlug}/tiers/${tierId}`),

  /** Create a new tier */
  create: (siteSlug: string, data: CreateTierRequest) =>
    api.post<ApiResponse<SubscriptionTier>>(`/v1/sites/${siteSlug}/tiers`, data),

  /** Delete a tier */
  delete: (siteSlug: string, tierId: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/tiers/${tierId}`),
};
